// What a failed Bulk Enquiry call means for the page: its message and what to
// do next. The backend's error body is `{ status, message, ...extras }` (core
// helpers/request.js: a FlowError's extras, such as `code` or `rows`, sit next
// to the message); a network failure or a timeout has no status at all.
// Messages the backend writes for HR are shown as they are; the ones that
// name internals (or none) get a plain sentence here.
import { BLOCKING_REASON_LABEL, STAGE_LIMITS, megabytes } from './constants'

const GENERIC = 'Something went wrong. Try again.'
const UPLOAD_FAILED = 'Upload failed. Try again.'
const SESSION_EXPIRED = 'Your session expired. Log in again.'
const ROW_GONE = 'This row is no longer waiting for files.'
const BATCH_GONE = 'This batch is not available to you any more.'

/** `{ status, code, message, extras }` of any rejected API call (status null: network, timeout). */
export function apiError(err) {
  if (!err || typeof err !== 'object') {
    return { status: null, code: null, message: typeof err === 'string' ? err : '', extras: {} }
  }
  const { status, code, message, ...extras } = err
  return {
    status: Number.isInteger(status) ? status : null,
    code: typeof code === 'string' && code ? code : null,
    message: typeof message === 'string' ? message : '',
    extras,
  }
}

// "Row not found" (a row of this batch that is gone) vs "Batch not found"
// (the batch, or it left the caller's bulk ∩ create scope): both 404, no code.
const isRowGone = (message) => /^row not found/i.test(message)

/**
 * A failed file stage (POST /:batchId/rows/:rowId/files/:kind), for one item of
 * the upload queue: `{ message, retryable, fatal, code, reload }`.
 *   retryable  sending the same file again may work (network, server error)
 *   fatal      the next uploads would fail the same way (batch closed or
 *              gone, permission lost, session over): stop the queue
 *   reload     'rows' (the row changed: re-read the rows waiting for files)
 *              or 'batch' (re-read the batch itself), else null
 */
export function stageError(err) {
  const { status, code, message, extras } = apiError(err)
  const out = (fields) => ({ retryable: false, fatal: false, code, reload: null, ...fields })

  if (status === 401) return out({ message: SESSION_EXPIRED, fatal: true })
  if (status === 403) return out({ message: message || 'You do not have permission to change these files.', fatal: true })
  if (status === 404) {
    return isRowGone(message)
      ? out({ message: ROW_GONE, reload: 'rows' })
      : out({ message: BATCH_GONE, fatal: true, reload: 'batch' })
  }
  if (status === 409) {
    switch (code) {
      case 'batch_not_editable':
        return out({ message: message || 'This batch no longer takes files.', fatal: true, reload: 'batch' })
      case 'row_not_editable':
      case 'file_not_required':
        return out({ message: message || ROW_GONE, reload: 'rows' })
      case 'ktp_photo_in_use': {
        const rows = Array.isArray(extras.rows) ? extras.rows : []
        return out({
          message: rows.length ? `The same photo is already saved on row ${rows.join(', ')}.` : message,
        })
      }
      default:
        // ktp_reused, file_nik_mismatch and any later 409: the backend's words.
        return out({ message: message || GENERIC })
    }
  }
  if (status === 413) {
    const photo = megabytes(STAGE_LIMITS.photo.maxBytes)
    const doc = megabytes(STAGE_LIMITS.document.maxBytes)
    return out({ message: `The file is larger than the limit (KTP photo ${photo}, document ${doc}).` })
  }
  if (status === 422) {
    if (/invalid file type/i.test(message)) return out({ message: 'This file type is not accepted here.' })
    return out({ message: validationText(extras, message) })
  }
  if (status === 429) return out({ message: 'Too many uploads at once. Wait a moment, then retry.', retryable: true })
  // 400 (no file part: a client fault), 5xx (media-service or database down),
  // a network failure or a timeout: the same file may well go through later.
  return out({ message: UPLOAD_FAILED, retryable: true })
}

/**
 * A failed removal of a row's saved file (DELETE …/files/:kind): the stage
 * refusals mean the same there (batch closed, row processed, a reused
 * e-KTP). `{ message, reload }`.
 */
export function unstageError(err) {
  const { status } = apiError(err)
  const failure = stageError(err)
  if (failure.retryable || status === null) return { message: 'The file could not be removed. Try again.', reload: null }
  return { message: failure.message, reload: failure.reload }
}

const validationText = (extras, message) => {
  const details = Array.isArray(extras.errors) ? extras.errors.map((e) => e?.message).filter(Boolean) : []
  return details.length ? details.join(' ') : message || GENERIC
}

// Codes of a Process pre-check refusal that are one of the batch's
// blocking_reasons: the same words as the detail page.
const REFUSAL_BLOCKING = ['inactive', 'no_subscription', 'completed', 'canceled', 'no_ready_rows']

/**
 * POST /:batchId/process refused before its stream started (the pre-checks
 * answer plain JSON, enquiry_import.controller prepareProcess), or its
 * connection failing before the stream did. `{ code, message, retry, extras }`:
 *   code   the backend's code (processing, consent_config_changed, inactive,
 *          no_subscription, billing_unavailable, no_create_permission,
 *          completed, canceled, no_ready_rows), else not_found, session,
 *          permission, network, busy or error
 *   retry  sending it again later may work (503, network, 429, 5xx)
 *   extras the rest of the body: consent_changes + resync_allowed,
 *          member_status, processing { started_at, heartbeat_at }
 * 409 processing is no failure: another run holds the batch (the caller
 * follows it).
 */
export function processRefusal(err) {
  const { status, code, message, extras } = apiError(err)
  const out = (fields) => ({ code: code || 'error', message: message || GENERIC, retry: false, extras, ...fields })

  if (status === null) {
    return out({
      code: 'network',
      message: 'The connection failed before processing started. Check the batch status, then try again.',
      retry: true,
    })
  }
  if (status === 401) return out({ code: 'session', message: SESSION_EXPIRED })
  if (status === 404) return out({ code: 'not_found', message: 'Batch not found: it no longer exists or is out of your company access.' })
  if (status === 402) return out({ code: 'no_subscription', message: BLOCKING_REASON_LABEL.no_subscription })
  if (code === 'no_create_permission') {
    return out({ message: "Processing needs the Create Enquiry permission for this batch's company." })
  }
  if (REFUSAL_BLOCKING.includes(code)) return out({ message: BLOCKING_REASON_LABEL[code] })
  if (status === 403) return out({ code: code || 'permission', message: message || 'You do not have permission to process this batch.' })
  if (status === 409) return out({ message: message || BLOCKING_REASON_LABEL[code] || GENERIC })
  if (status === 422) return out({ code: 'error', message: validationText(extras, message) })
  if (status === 429) return out({ code: 'busy', message: 'Too many requests. Wait a moment, then try again.', retry: true })
  // 503 billing_unavailable keeps the backend's words ("…no row was processed").
  if (status === 503) return out({ retry: true })
  return out({ code: 'error', message: GENERIC, retry: status >= 500 })
}

// The permission the batch's company needs for an action that creates
// enquiries (403 no_create_permission: the user sees the batch, the grant is
// missing for its company).
const NO_CREATE_PERMISSION = {
  retry: "Retrying failed rows needs the Create Enquiry permission for this batch's company.",
  remove: "Removing rows needs the Create Enquiry permission for this batch's company.",
}

/**
 * A refused batch action: retry-failed, cancel, resync-config or remove (a
 * row). `{ message, reload }`; reload: the batch changed under the page
 * (processing, completed or canceled elsewhere, a row gone), read it again.
 * A 409 carries the backend's own sentence ("Only an open batch can be
 * cancelled (this one is completed).", reupload_required, …).
 */
export function actionError(action, err) {
  const { status, code, message, extras } = apiError(err)
  if (status === null) return { message: GENERIC, reload: false }
  if (status === 401) return { message: SESSION_EXPIRED, reload: false }
  if (status === 403) {
    if (code === 'no_create_permission') return { message: NO_CREATE_PERMISSION[action] || message, reload: false }
    return { message: message || 'You do not have permission to do this.', reload: false }
  }
  if (status === 404) {
    if (action === 'remove' && isRowGone(message)) return { message: 'This row was already removed.', reload: true }
    return { message: 'Batch not found: it no longer exists or is out of your company access.', reload: true }
  }
  if (status === 409) return { message: message || GENERIC, reload: true }
  if (status === 422) return { message: validationText(extras, message), reload: false }
  if (status === 429) return { message: 'Too many requests. Wait a moment, then try again.', reload: false }
  return { message: GENERIC, reload: false }
}
